import { Match, Router, withRouter } from 'found';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { deleteItemFromCollection } from '../actions/collections.actions';
import { ContentItem } from '../types/AppContent';
import InfiniteScroll from 'react-infinite-scroller';
import ProductMuiCard from './cards/MuiCard/ProductMuiCard/ProductMuiCard';
import { getProductMuiCardProps } from '../utils/getCommonCardProps';
import { getMindItemMuiCardProps } from '../utils/getCommonCardProps';
import MindItemMuiCard from './cards/MuiCard/MindItemMuiCard/MindItemMuiCard';
import VideoMuiCard from './cards/MuiCard/VideoMuiCard/VideoMuiCard';
import { getVideoMuiCardProps } from '../utils/getCommonCardProps';
import CourseMuiCard from './cards/MuiCard/CourseMuiCard/CourseMuiCard';
import { getCourseMuiCardProps } from '../utils/getCommonCardProps';
import { Alert } from '../components/NotificationAlert/NotificationAlert';
import EmptySearchStateImage from './Search/SearchResults/EmptySearchResultsNewUI/EmptySearchStateImage';
import { Box } from '@mui/material';
import { spaces } from '../assets/styles/themes/tokens';
import SingleSearchResultContainer from './Search/SearchResults/SingleSearchResultContainer';
import SearchResultsContainer from './Search/SearchResults/SearchResultsContainer';
import useResponsivePadding from '../utils/useResponsivePadding';

interface ItemsListOwnProps {
  activeId?: string;
  items: ContentItem[];
  linkRoot: string[];
  setActiveId?: (id: string) => void;
  isLoading: boolean;
  hasNext: boolean;
  loadNext: () => void;
  total: number;
  onShowNotification?: (alert: Alert) => void;
}

interface ItemsListProps extends ItemsListOwnProps {
  deleteItemFromCollection: typeof deleteItemFromCollection;
  match: Match;
  router: Router;
}

const ItemsList: React.FC<ItemsListProps> = ({ items, ...props }) => {
  const contentMargin = useResponsivePadding();

  // Deprecated legacy values from now removed variables.scss file
  const COLLECTION_LIST_HEIGHT = 84;
  const COLLECTION_GRID_GAP = 15;
  const THRESHOLD = (COLLECTION_LIST_HEIGHT + COLLECTION_GRID_GAP) * 5;

  const infiniteScrollProps = {
    threshold: THRESHOLD,
    className: 'grid-container--new-merkliste',
    loadMore: () => props.loadNext(),
    hasMore: props.hasNext && !props.isLoading,
    useWindow: false,
    // getScrollParent does not work with this query selector,
    // so it has been commented out for now.
    // getScrollParent: () => document.querySelector('.main-container-grid__body') as HTMLElement | null,
  };

  if (props.total === 0) return <NewMerklisteEmptyState />;

  const renderNewMerklisteItem = (item: ContentItem, isFirstElement: boolean) => {
    if (item.type === 'product') {
      return <ProductMuiCard 
        key={item.id} 
        {...getProductMuiCardProps(item)} 
        isNewAddMenuButtonMenu={true}
        onShowNotification={props.onShowNotification}
        isFirstElement={isFirstElement}
      />;
    }
    if (item.type !== 'mindItem') return null;
    
    switch (item.fileType) {
      case 'document': {
        return <MindItemMuiCard 
          key={item.id} 
          {...getMindItemMuiCardProps(item)} 
          isNewAddMenuButtonMenu={true}
          onShowNotification={props.onShowNotification}
          isFirstElement={isFirstElement}
        />;
      }
      case 'video': {
        return <VideoMuiCard 
          key={item.id} 
          {...getVideoMuiCardProps(item)} 
          isNewAddMenuButtonMenu={true}
          onShowNotification={props.onShowNotification}
          isFirstElement={isFirstElement}
        />;
      }
      case 'course': {
        return <CourseMuiCard 
          key={item.id} 
          {...getCourseMuiCardProps(item)} 
          isNewAddMenuButtonMenu={true}
          onShowNotification={props.onShowNotification}
          isFirstElement={isFirstElement}
        />;
      }
      default: return null;
    }
  };

  return (
    <InfiniteScroll {...infiniteScrollProps}>
      <SearchResultsContainer
        data-testid='items-list-container'
        customSx={{ marginTop: contentMargin, gap: spaces.md, width: '100%' }}
      >
        {items.map((item, index) => {
          return (
            <Box key={item.id}>
              <SingleSearchResultContainer
                key={item.id}
                dividerSx={{ marginTop: spaces.md }}
                isLastElement={index === items.length - 1}
              >
                {renderNewMerklisteItem(item, index === 0)}
              </SingleSearchResultContainer>
            </Box>
          );
        })}
      </SearchResultsContainer>
    </InfiniteScroll>
  );
};

const NewMerklisteEmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box data-testid='new-merkliste-empty-state' sx={{ paddingTop: spaces.xl }}>
      <EmptySearchStateImage 
        title={t('No materials in Merkliste')} 
        subtitle={t('You have not saved any materials in Merkliste yet')} />
    </Box>
  );
};

export default compose<React.FC<ItemsListOwnProps>>(
  withRouter,
  connect(
    undefined,
    { deleteItemFromCollection },
  ),
)(ItemsList);
